import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useEffect, useState } from 'react';

import { useBrowsing } from 'hooks/use-browsing';

import { PUBLIC_GTM_ID_ENV } from 'tools/environments';

import GoogleTagManager from 'core/generic/helpers/google-tag-manager';

import { Layout } from '@feed/layout/layout';
import { SessionProvider } from '@feed/session';
import { WidgetConfigProvider } from '@feed/session/config';
import env from '@feed/tools/environments';

import '../styles/globals.scss';
import './styles.css';

type ComponentWithPageLayout = AppProps & {
	Component: AppProps['Component'] & {
		bareLayout?: boolean;
	};
};

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
			refetchOnWindowFocus: false,
			// Without this every query defaults to staleTime: 0, so remounting
			// a component (e.g. navigating back to the feed) always refetches
			// even when the data is seconds old. Queries that need to react to
			// a specific event (widget results on completion, generated-image
			// polling) already invalidate/refetch explicitly and are
			// unaffected by this.
			staleTime: 30_000,
		},
	},
});

function CustomApp({ Component, pageProps }: ComponentWithPageLayout) {
	const { isReady, query } = useBrowsing();
	const [loginWithReturnUrl, setLoginWithReturnUrl] = useState('');
	const [canonicalUrl, setCanonicalUrl] = useState(
		`${env.PUBLIC_API_BASE || ''}${env.PUBLIC_BASE_PATH || '/feed'}`
	);

	useEffect(() => {
		const baseLogin = env.PUBLIC_LOGIN_URL_ENV;
		if (baseLogin) {
			setLoginWithReturnUrl(
				`${baseLogin}?returnUrl=${window.location.href}`
			);
		}

		const href = document.location.href;
		if (href.includes(`${env.PUBLIC_BASE_PATH || '/feed'}/widget`)) {
			setCanonicalUrl(href);
		}
	}, []);

	const apiBase =
		env.PUBLIC_API_BASE ||
		env.PUBLIC_API_URL_ENV.replace(/\/web3\/api$/, '') ||
		'';

	const page = <Component {...pageProps} />;

	return (
		<QueryClientProvider client={queryClient}>
			<WidgetConfigProvider
				baseApiUrl={apiBase}
				widgetApiUrl={env.PUBLIC_API_WIDGETS}
				interactionUrl={env.PUBLIC_API_INTERACTION}
				imagePath={env.PUBLIC_IMAGE_URL}
				loginUrl={loginWithReturnUrl}
				googleMapsApiKey={env.PUBLIC_GOOGLE_MAP_API || ''}
			>
				<SessionProvider assetsApiUrl={env.PUBLIC_API_ASSETS_URL || ''}>
					<Head>
						<meta
							name="viewport"
							content="width=device-width, initial-scale=1.0"
						/>
						<meta name="author" content="Manchester City" />
						<meta
							name="theme-color"
							content="var(--colour-background-core-secondary)"
						/>
						<link rel="canonical" href={canonicalUrl} />
						<link rel="manifest" href="/feed/manifest.json" />
					</Head>

					{Component.bareLayout ? (
						<>
							{isReady &&
								query.fromapp === undefined &&
								PUBLIC_GTM_ID_ENV && (
									<GoogleTagManager
										gtmId={PUBLIC_GTM_ID_ENV}
									/>
								)}
							{page}
						</>
					) : (
						<Layout>{page}</Layout>
					)}
				</SessionProvider>
			</WidgetConfigProvider>

			{process.env.NODE_ENV === 'development' && (
				<ReactQueryDevtools initialIsOpen={false} />
			)}
		</QueryClientProvider>
	);
}

export default CustomApp;
